import { useState } from "react";
import { mutate as globalMutate } from "swr";
import { encodeFunctionData, Hex } from "viem";
import { useAccount } from "wagmi";
import { useWriteContract } from "~/core/hooks/useWriteContract";
import { useSound } from "../components/SoundHandler";
import { useError } from "~/core/contexts/ErrorContext";
import { useToast } from "~/core/contexts/ToastContext";
import { findAlreadyClaimedGames } from "../claimDiagnostics";
import { BlinkoGame } from "../api";
import { MULTICALL3_CONTRACT_ADDRESS, COMMIT_REVEAL_CONTRACT_ADDRESS, ZERO_ADDRESS } from "~/core/constants";
import { TOKENS } from "~/core/tokens";
import { formatBalance } from "~/core/utils/formatBalance";
import { icons } from "../assets/icons/icons";
import { ContainerShape, HexagonShape, InnerShape } from "../components/Shape";
import { MULTICALL3_ABI } from "~/core/abis/multicall3.abi";
import { COMMIT_REVEAL_ABI } from "~/core/abis/commit.reveal.abi";
import { SESSION_KEYS } from "~/core/contexts/AbsSessionContext";
import Loader from "~/core/components/Loader";
import { useAllBalance } from "~/core/hooks/useAllBalance";
import { useBlinkoContract } from "../hooks/useBlinkoContract";

export const ClaimModal = ({ games: _games, onDismiss }: { games: BlinkoGame[], onDismiss: () => void }) => {
  const sounds = useSound();
  const { writeContractAsync } = useWriteContract();
  const { handleError } = useError();
  const { mutate } = useAllBalance()
  const { cashOutOnChain, markAsLostOnChain } = useBlinkoContract()
  const { address } = useAccount();
  const toast = useToast();

  const [isLoading, setIsLoading] = useState(false);

  const onClose = () => {
    onDismiss();
    sounds.play('click');
  };

  const onClaim = async () => {
    if (isLoading) {
      return;
    }
    sounds.play('click');
    setIsLoading(true);

    try {
      if (_games.length === 0) {
        throw new Error("No games to settle");
      }

      if (_games.length === 1) {
        // Optimized single game claim using BLINKO session key
        const game = _games[0];
        const payout = BigInt(game.payoutAmount || '0');
        const isWin = game.status === 'won' || payout > 0n;

        if (isWin) {
          await cashOutOnChain({
            gameId: game.gameId as Hex,
            payoutAmount: payout,
            gameState: game.gameStateHash as Hex,
            gameSeed: game.gameSeed as Hex,
            deadline: BigInt(game.deadline),
            serverSignature: game.cashOutSignature as Hex,
          });
        } else {
          await markAsLostOnChain({
            gameId: game.gameId as Hex,
            gameState: game.gameStateHash as Hex,
            gameSeed: game.gameSeed as Hex,
            deadline: BigInt(game.deadline),
            serverSignature: game.cashOutSignature as Hex,
          });
        }
      } else {
        // Batch claim using Multicall (requires connected wallet usually, or NONE session logic)
        const calls = []

        for (const game of _games) {
          const payout = BigInt(game.payoutAmount || '0');
          const isWin = game.status === 'won' || payout > 0n;

          if (isWin) {
            calls.push({
              target: COMMIT_REVEAL_CONTRACT_ADDRESS,
              allowFailure: false,
              callData: encodeFunctionData({
                abi: COMMIT_REVEAL_ABI as any,
                functionName: 'cashOut',
                args: [
                  game.gameId as Hex,
                  payout,
                  game.gameStateHash as Hex,
                  game.gameSeed as Hex,
                  BigInt(game.deadline),
                  game.cashOutSignature as Hex,
                ]
              })
            });
          } else {
            calls.push({
              target: COMMIT_REVEAL_CONTRACT_ADDRESS,
              allowFailure: false,
              callData: encodeFunctionData({
                abi: COMMIT_REVEAL_ABI as any,
                functionName: 'markGameAsLost',
                args: [
                  game.gameId as Hex,
                  game.gameStateHash as Hex,
                  game.gameSeed as Hex,
                  BigInt(game.deadline),
                  game.markAsLostSignature,
                ]
              })
            });
          }
        }

        if (calls.length === 0) {
          throw new Error("No valid games to settle");
        }

        await writeContractAsync({
          sessionKey: SESSION_KEYS.NONE,
          address: MULTICALL3_CONTRACT_ADDRESS,
          abi: MULTICALL3_ABI as any,
          functionName: 'aggregate3',
          args: [calls],
        });
      }
    } catch (e) {
      console.error('Failed to settle games', e);

      // The batch settles via Multicall3 `aggregate3` with allowFailure:false,
      // so a single already-settled game reverts the whole thing with an opaque
      // "Multicall3: call failed". Probe each game to see if the real cause is
      // games that were already cashed out (indexer still listed them as
      // claimable) and surface that precisely instead of a raw error.
      const alreadyClaimed = address
        ? await findAlreadyClaimedGames(_games, address).catch(() => new Set<string>())
        : new Set<string>();

      if (alreadyClaimed.size > 0) {
        toast.error(
          alreadyClaimed.size >= _games.length
            ? 'Already cashed out — these winnings are settled.'
            : `${alreadyClaimed.size} of these games were already cashed out, which blocked the batch. Refreshing — tap Cash Out again.`
        );
        // Revalidate the claimable list so the stale game drops and a retry works.
        if (address) globalMutate([address]);
      } else {
        handleError('Failed to claim winnings', e);
      }
    } finally {
      setIsLoading(false);
      mutate();
      onClose();
    }
  };

  // Calculate total win amount found
  const totalWin = _games.reduce((acc, game) => acc + BigInt(game.payoutAmount || '0'), 0n);

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-[#18181B]/50">
      <div>
        <ContainerShape cornerDiagonal={20} cornerRadius={8} strokeWidth={2}>
          <div className="p-6 w-[350px] md:w-[448px]">
            <div className="flex flex-row justify-between items-start">
              <div className="mt-1 text-2xl font-black text-white font-blinko">
                Forgot Something?
              </div>
              <button
                onClick={onClose}
                className=""
              >
                <HexagonShape cornerRadius={6} strokeWidth={1}>
                  <div className="flex justify-center items-center p-2">
                    <img src={icons.close} className="w-6 h-6" />
                  </div>
                </HexagonShape>
              </button>
            </div>
            <p className="text-sm text-white/70 mt-3">
              You left <span className="text-green-400">{formatBalance(totalWin, TOKENS[ZERO_ADDRESS].decimals)} ETH</span> behind in a game.
            </p>

            <div className="mt-6 flex justify-end space-x-2">
              <button onClick={onClaim} disabled={isLoading} className="disabled:opacity-50">
                <InnerShape cornerDiagonal={14} cornerRadius={8} strokeWidth={1}>
                  <div className="relative px-6 py-3 flex items-center gap-2">
                    {isLoading 
                      ? <Loader size="16px" stroke="white" /> 
                      : (
                      <p className="text-sm font-medium text-white font-blinko">
                        Cash Out
                      </p>
                    )}
                  </div>
                </InnerShape>
              </button>
            </div>
          </div>
        </ContainerShape>
      </div>
    </div>
  );
};
